import React from "react";
import { Box, IconButton, Stack, TextField, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ComboBox from "../../../../component/common/ComboBox";

/**
 * 반복 행 입력 — 품목 목록, 용역인 명단, 지출 예정처럼 줄 수가 정해지지 않은 칸.
 *
 * 예전 양식은 이런 표가 없어서 "품목 3개"를 텍스트 한 칸에 몰아 적었고, 그래서 합계 금액이
 * 사람 손 계산이었다. 표로 받으면 합계를 화면이 계산해 줄 수 있다.
 */
export default function GridField({ field, value, onChange }) {
    const rows = Array.isArray(value) ? value : [];
    const cols = field.columns || [];

    const setCell = (i, key, v) => {
        const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r));
        onChange(next);
    };
    const addRow = () => onChange([...rows, {}]);
    const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

    return (
        <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                    {field.label}
                    {field.required && <Box component="span" sx={{ color: "#dc2626", ml: 0.4 }}>*</Box>}
                </Typography>
                <IconButton size="small" onClick={addRow} sx={{ color: "#2563eb" }}>
                    <AddRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Stack>

            {rows.length === 0 && (
                <Box onClick={addRow} sx={{
                    border: "1px dashed #cbd5e1", borderRadius: 1, py: 1.5,
                    textAlign: "center", cursor: "pointer",
                    fontSize: 12, color: "#94a3b8",
                    "&:hover": { borderColor: "#2563eb", color: "#2563eb" },
                }}>
                    + 행 추가
                </Box>
            )}

            {rows.map((row, i) => (
                <Stack key={i} direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                    {cols.map((c) => (
                        <Box key={c.key} sx={{ flex: 1, minWidth: 0 }}>
                            {i === 0 && (
                                <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mb: 0.25 }}>{c.label}</Typography>
                            )}
                            {c.type === "SELECT" ? (
                                <ComboBox
                                    clearable={false}
                                    value={row[c.key] ?? ""}
                                    onChange={(v) => setCell(i, c.key, v)}
                                    options={(c.options || []).map((o) => ({ value: o, label: o }))}
                                />
                            ) : (
                                <TextField
                                    size="small" fullWidth
                                    type={c.type === "MONEY" || c.type === "NUMBER" ? "number" : c.type === "DATE" ? "date" : "text"}
                                    value={row[c.key] ?? ""}
                                    onChange={(e) => setCell(i, c.key, e.target.value)}
                                    InputProps={{ sx: { fontSize: 12.5 } }}
                                />
                            )}
                        </Box>
                    ))}
                    <Box sx={{ pt: i === 0 ? 2 : 0 }}>
                        <IconButton size="small" onClick={() => removeRow(i)} sx={{ color: "#cbd5e1", "&:hover": { color: "#dc2626" } }}>
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Box>
                </Stack>
            ))}
        </Box>
    );
}
